import { todayCivilDate } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'
import type { CivilDateRange } from '@sportninja/common/features/game/gameListView/types'
import { DateTime } from 'luxon'

import type { CalendarSubView } from '../hooks/useGameViewMode'

const UTC = 'UTC'

/*
 * The fallback is the VIEWER's civil date, read through the same helper the
 * calendar renders with. Defaulting to `DateTime.utc()` here while the viewport
 * defaulted to the local date meant that, on an evening west of Greenwich at a
 * month boundary, the grid drew one month and the query fetched the next.
 */
const todayAnchor = (): DateTime =>
  DateTime.fromISO(todayCivilDate(), { zone: UTC })

const anchorOf = (anchorDate: string | null): DateTime => {
  if (anchorDate === null) {
    return todayAnchor()
  }
  const parsed = DateTime.fromISO(anchorDate, { zone: UTC })
  return parsed.isValid ? parsed : todayAnchor()
}

const format = (value: DateTime): string => value.toFormat('yyyy-MM-dd')

/**
 * The civil window a calendar layout is actually showing.
 *
 * Derived in the PARENT from the URL anchor rather than reported up by the
 * child, so the query window and the rendered viewport come from one source. A
 * child-reports-up arrangement has a render where the two disagree, and that
 * render is the one that fires a fetch for the wrong month.
 *
 * The month case covers the whole 42-cell grid, not just the calendar month:
 * the leading and trailing cells are real days and would otherwise always look
 * empty.
 */
export const calendarViewportRange = (
  anchorDate: string | null,
  subView: CalendarSubView
): CivilDateRange | null => {
  const anchor = anchorOf(anchorDate)

  if (subView === 'day') {
    return {
      start: format(anchor),
      endExclusive: format(anchor.plus({ days: 1 })),
    }
  }

  if (subView === 'week') {
    const weekStart = anchor.minus({ days: anchor.weekday % 7 })
    return {
      start: format(weekStart),
      endExclusive: format(weekStart.plus({ days: 7 })),
    }
  }

  if (subView === 'month') {
    const firstOfMonth = anchor.startOf('month')
    const gridStart = firstOfMonth.minus({ days: firstOfMonth.weekday % 7 })
    return {
      start: format(gridStart),
      endExclusive: format(gridStart.plus({ days: 42 })),
    }
  }

  //  Agenda has no viewport of its own — it lists whatever the filters select,
  //  which is exactly the legacy behaviour.
  return null
}
